import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { fetchMilestoneTrackerData } from "../services/milestoneTrackerApi";
import { mapMilestoneToTableRow, normalizeInvoices, toUiInvoiceStatus, toFieldglassStatus } from "../utils/milestoneDataMapper";
import { toAmount, safeCalc } from "../utils/maskedValue";

const DEFAULT_SUMMARY = {
	totalMilestoneCount: 0,
	totalInvoiceCount: 0,
	totalMilestoneAmount: 0,
	submittedCount: 0,
	submittedAmount: 0,
	approvedCount: 0,
	approvedAmount: 0,
	pendingCount: 0,
	pendingInvoiceCount: 0,
	pendingInvoiceAmount: 0,
	invoiceMismatch: 0,
	mismatchCount: 0,
	mismatchAmount: 0,
};

const MONTH_MAP = {
	jan: 1,
	feb: 2,
	mar: 3,
	apr: 4,
	may: 5,
	jun: 6,
	jul: 7,
	aug: 8,
	sep: 9,
	oct: 10,
	nov: 11,
	dec: 12,
};

const parseMonthYear = (value) => {
	if (!value) return null;
	const raw = value.trim();
	if (!raw) return null;

	const monthText = /^([a-zA-Z]{3})[-\s/](\d{2}|\d{4})$/.exec(raw);
	if (monthText) {
		const month = MONTH_MAP[monthText[1].toLowerCase()];
		if (!month) return null;
		const yearPart = monthText[2];
		const year = yearPart.length === 2 ? 2000 + Number(yearPart) : Number(yearPart);
		return { month, year };
	}

	const ymd = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/.exec(raw);
	if (ymd) {
		return { month: Number(ymd[2]), year: Number(ymd[1]) };
	}

	const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
	if (mdy) {
		return { month: Number(mdy[1]), year: Number(mdy[3]) };
	}

	const dmy = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(raw);
	if (dmy) {
		return { month: Number(dmy[2]), year: Number(dmy[3]) };
	}

	const fallback = new Date(raw);
	if (!Number.isNaN(fallback.getTime())) {
		return {
			month: fallback.getMonth() + 1,
			year: fallback.getFullYear(),
		};
	}

	return null;
};

export function useTrackerData(pageNumber = 0, pageSize = 10) {
	const [milestoneRows, setMilestoneRows] = useState([]);
	const [invoiceRows, setInvoiceRows] = useState([]);
	const [summary, setSummary] = useState(DEFAULT_SUMMARY);
	const [loadingData, setLoadingData] = useState(false);
	const [loadError, setLoadError] = useState("");
	const [hasLoaded, setHasLoaded] = useState(false);
	// Every reload gets a new sequence number, so Retry or a mutation refresh of the same page is a new request.
	const [reloadSeq, setReloadSeq] = useState(0);
	const reloadSeqRef = useRef(0);
	// Key of the last request that finished (success or failure). The current key is derived in the render
	// that changes page/size/reload, so the first render of a new request already reports loading.
	const [settledKey, setSettledKey] = useState(null);
	const requestKey = `${pageNumber}|${pageSize}|${reloadSeq}`;

	const reloadTrackerData = useCallback(async () => {
		const seq = ++reloadSeqRef.current;
		const key = `${pageNumber}|${pageSize}|${seq}`;
		setReloadSeq(seq);
		try {
			setLoadingData(true);
			setLoadError("");
			setMilestoneRows([]);
			setInvoiceRows([]);

			const data = await fetchMilestoneTrackerData(pageNumber, pageSize);
			if (seq !== reloadSeqRef.current) return;
			const invoices = Array.isArray(data.invoices) ? data.invoices : [];
			const milestones = Array.isArray(data.milestones) ? data.milestones : [];
			const fallbackProjectId = milestones[0]?.projectId || "-";

			setInvoiceRows(normalizeInvoices(invoices, fallbackProjectId));
			setMilestoneRows(milestones.map(mapMilestoneToTableRow));
			setSummary({
				totalMilestoneCount: Number(data.totalMilestoneCount || milestones.length || 0),
				totalInvoiceCount: Number(
					data.totalInvoiceCount ||
					data.totalInvoices ||
					data.totalInvoiceRecords ||
					invoices.length ||
					0,
				),
				totalMilestoneAmount: toAmount(data.totalMilestoneAmount),
				submittedCount: Number(data.submittedCount || 0),
				submittedAmount: toAmount(data.submittedAmount),
				approvedCount: Number(data.approvedCount || 0),
				approvedAmount: toAmount(data.approvedAmount),
				pendingCount: Number(data.pendingCount || 0),
				pendingInvoiceCount: Number(data.pendingInvoiceCount || 0),
				pendingInvoiceAmount: safeCalc([data.pendingInvoiceAmount], Math.abs),
				invoiceMismatch: safeCalc([data.invoiceMismatch], Math.abs),
				mismatchCount: Number(data.pendingCount || 0),
				mismatchAmount: safeCalc([data.invoiceMismatch], Math.abs),
			});
			setHasLoaded(true);
			setSettledKey(key);
		} catch (error) {
			if (seq !== reloadSeqRef.current) return;
			const msg = error instanceof Error ? error.message : "Failed to load milestones";
			setLoadError(msg);
			setSettledKey(key);
		} finally {
			if (seq === reloadSeqRef.current) setLoadingData(false);
		}
	}, [pageNumber, pageSize]);

	useEffect(() => {
		void reloadTrackerData();
	}, [reloadTrackerData]);

	return {
		milestoneRows,
		invoiceRows,
		summary,
		loadingData,
		loadError,
		hasLoaded,
		// True from the render that starts a request until it settles (page, size or reload changed).
		isRequestPending: settledKey !== requestKey,
		reloadTrackerData,
	};
}

export function useTrackerFilters({
  milestoneRows,
  invoiceRows,
  search,
  statusFilter,
  milestoneStatusFilter,
	projectFilter,
	sowFilter,
	revenueMonthFilter,
	revenueYearFilter,
}){
	const selectedMonth = revenueMonthFilter === "all" ? null : Number(revenueMonthFilter);
	const selectedYear = revenueYearFilter === "all" ? null : Number(revenueYearFilter);

  const filteredMilestones = useMemo(() => {
    return milestoneRows.filter((r) => {
      const q = search.toLowerCase();
			const matchProject = projectFilter === "all" || (r.projectId || "") === projectFilter;
			const matchSow = sowFilter === "all" || (r.sowId || "") === sowFilter;
			const milestoneMonthYear = parseMonthYear(r.startDate || r.endDate || r.submittedDate);
			const matchMonth = !selectedMonth || milestoneMonthYear?.month === selectedMonth;
			const matchYear = !selectedYear || milestoneMonthYear?.year === selectedYear;

const milestoneStatus = r.status || toUiInvoiceStatus(
        undefined,
        r.invoiceStatus,
        Boolean(r.submittedDate),
      );

      const matchStatus =
        milestoneStatusFilter === "all" ||
        milestoneStatus === milestoneStatusFilter ||
        (milestoneStatusFilter === "Invoiced" && milestoneStatus === "In Review") ||
        (milestoneStatusFilter === "Pending Approval" && milestoneStatus === "Pending") ||
        (milestoneStatusFilter === "Rejected" && milestoneStatus === "Rejected");

      return (
				(!q ||
					(r.sowId || "").toLowerCase().includes(q) ||
					(r.projectId || "").toLowerCase().includes(q) ||
					(r.manager || "").toLowerCase().includes(q) ||
					(r.accountId || "").toLowerCase().includes(q) ||
					(r.milestoneName || "").toLowerCase().includes(q)) &&
				matchStatus &&
				matchProject &&
				matchSow &&
				matchMonth &&
				matchYear
      );
    });
	}, [milestoneRows, search, statusFilter, projectFilter, sowFilter, selectedMonth, selectedYear]);

const filteredInvoices = useMemo(() => {
    return invoiceRows.filter((r) => {
      const q = search.toLowerCase();

      const matchSearch =
        !q ||
        (r.invoiceId || "").toLowerCase().includes(q) ||
        (r.projectId || "").toLowerCase().includes(q) ||
        (r.sowId || "").toLowerCase().includes(q) ||
        (r.milestoneName || "").toLowerCase().includes(q);

			const matchProject = projectFilter === "all" || (r.projectId || "") === projectFilter;
			const matchSow = sowFilter === "all" || (r.sowId || "") === sowFilter;

      const fieldglassStatus = toFieldglassStatus(
        r.approvalStatusInFieldglass,
        r.invoicingStatus,
      );

			const matchStatus =
				statusFilter === "all" ||
				statusFilter === fieldglassStatus;

			const invoiceMonthYear = parseMonthYear(r.revenueMonth || r.invoiceSubmittedDate);
			const matchMonth = !selectedMonth || invoiceMonthYear?.month === selectedMonth;
			const matchYear = !selectedYear || invoiceMonthYear?.year === selectedYear;

			return matchSearch && matchStatus && matchProject && matchSow && matchMonth && matchYear;
    });
	}, [invoiceRows, search, statusFilter, projectFilter, sowFilter, selectedMonth, selectedYear]);

  return {
    filteredMilestones,
    filteredInvoices,
  };
}